import React, { useContext } from 'react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { ELSButton } from '@els/els-component-button-react';
import moment from 'moment';
import { flowRight, isNil } from 'lodash';
import { useHistory } from 'react-router';
import {
  HesiExamRemediationSettingsDto,
  RecommendationAttemptDto,
  RecommendationAttemptStatusDto,
  RecommendationDto,
  RecommendationContentTypeDto,
  StatusWithDueDate,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import IconWithText from '../../components/icon-with-text/IconWithText.component';
import {
  ELSButtonIconPosition,
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { ELSDataTable } from '../../components/els.components';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { DATE_TIME_PRIMARY } from '../../constants/date.constants';
import {
  StatusDisplayMap
} from '../../constants/remediation.constants';
import {
  NavigateToContentProps,
  RemediationContentTypeConfigMap
} from '../../models/remediation.models';
import { StatusPill } from '../../components/status-pill/StatusPill.component';
import { AppContext } from '../../components/app/AppProvider.component';
import { RoutePath } from '../../components/app/app.constants';
import {
  getAttemptStatus,
  getHourAndMinuteFromSecond,
  getRecommendationStatusWithDueDate,
  handleNavigateToContent,
  handleNavigateToContentPerformance,
  handleResumeOrStartNewAttempt,
  tableDataRowHandler
} from '../../utilities/remediation.utilities';
import {
  getEbookRecommendationData,
  getEbookTitleWithPagesDisplay
} from '../../utilities/ebook.utilities';

type RecommendationOverviewPropsOnly = {
  recommendation: RecommendationDto;
  handleReturnNavigation: () => void;
}

export type RecommendationOverviewProps = RecommendationOverviewPropsOnly & ELSPropsFromModalService;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const RecommendationOverviewComponent = (props: RecommendationOverviewProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const history = useHistory();

  const getScoreThreshold = () => {
    const {
      recommendation
    } = props;

    const {
      remediationAssignmentWithSources
    } = baseState;

    if (!remediationAssignmentWithSources || !recommendation) {
      return null;
    }

    const hesiSettings = remediationAssignmentWithSources.remediationAssignment.settings as HesiExamRemediationSettingsDto;

    const activityTypeConfig = hesiSettings.activityTypes.find((_activityType) => {
      return _activityType.activityType === recommendation.assessmentGoalType;
    });
    if (!activityTypeConfig || !activityTypeConfig.contentSettings) {
      return null;
    }
    const contentTypeConfig = activityTypeConfig.contentSettings.find((contentSetting) => {
      return contentSetting.contentType === RecommendationContentTypeDto.HESI_CASE_STUDY;
    });

    if (!contentTypeConfig || isNil(contentTypeConfig.minScoreToPass)) {
      return null;
    }
    return `${Math.round(contentTypeConfig.minScoreToPass * 100)}%`;
  };

  type TableDataRow = RecommendationAttemptDto & {
    score: number;
    statusWithDueDate: StatusWithDueDate;
  };

  const getTableData = (): TableDataRow[] => {
    const {
      recommendation
    } = props;

    const {
      recommendationAttempts,
      attemptAssessmentSubmissions
    } = baseState;

    if (!recommendationAttempts || !recommendation) {
      return null;
    }
    const attempts = recommendationAttempts
      .filter((attempt) => {
        return attempt.recommendationId === recommendation.id;
      });

    if (!attempts.length) {
      return null;
    }

    return attempts
      .map((attempt): TableDataRow => {

        let submission = null;

        if (attemptAssessmentSubmissions?.length) {
          submission = attemptAssessmentSubmissions.find((sub) => {
            return sub.assessmentId === attempt.eolsAssessmentId;
          });
        }

        return {
          ...attempt,
          score: submission ? submission.score : null,
          statusWithDueDate: getAttemptStatus(attempt, baseState)
        };
      });
  };

  const getLastUnfinishedAttempt = () => {
    const {
      recommendation
    } = props;

    const {
      recommendationAttempts
    } = baseState;

    if (!recommendation || !recommendationAttempts || !recommendationAttempts.length) {
      return null;
    }
    return recommendationAttempts
      .filter((attempt) => {
        return attempt.recommendationId === recommendation.id;
      })
      .find((attempt) => {
        return attempt.status === RecommendationAttemptStatusDto.NOT_STARTED
          || attempt.status === RecommendationAttemptStatusDto.IN_PROGRESS;
      });
  };

  const inProgressAttempt = getLastUnfinishedAttempt();

  const getTitle = () => {
    if (!props.recommendation) {
      return null;
    }
    return props.recommendation.title;
  };

  const getTime = () => {
    if (!props.recommendation || !props.recommendation.estimatedTime) {
      return null;
    }
    return getHourAndMinuteFromSecond(moment.duration(props.recommendation.estimatedTime).asSeconds());
  };

  const tableData = getTableData();

  const status = getRecommendationStatusWithDueDate({
    recommendation: props.recommendation,
    baseState
  });

  const contentTypeConfig = RemediationContentTypeConfigMap[props.recommendation.contentType];

  const navigateButtonToContent = (row: TableDataRow) => {
    if (
      !RemediationContentTypeConfigMap[props.recommendation.contentType].isScored
      && [RecommendationAttemptStatusDto.COMPLETED_PASSED, RecommendationAttemptStatusDto.COMPLETED_FAILED]
        .includes(row.status)
    ) {
      return null;
    }

    const contentProps: NavigateToContentProps = {
      appState,
      appStateRef,
      setAppState,
      recommendation: props.recommendation,
      recommendationAttempt: row,
      assessment: baseState.eolsAssessment,
      quizSession: null,
      history,
      ref: RoutePath.REMEDIATION_STUDENT_HOME
    };

    if (
      [
        RecommendationAttemptStatusDto.COMPLETED_PASSED,
        RecommendationAttemptStatusDto.COMPLETED_FAILED
      ].includes(row.status)
    ) {
      return (
        <button
          type="button"
          className="u-els-anchorize"
          onClick={() => {
            handleNavigateToContentPerformance(contentProps);
          }}
        >
          View results
        </button>
      );
    }

    return (
      <button
        type="button"
        className="u-els-anchorize"
        onClick={() => {
          handleNavigateToContent(contentProps);
        }}
      >
        Continue
      </button>
    );
  };

  const recData = getEbookRecommendationData(props.recommendation);


  return (
    <div className="c-ssa-case-study-overview">

      <div className="o-els-container">
        <FlexLayout
          modifiers={[
            FlexLayoutModifier.GUTTERS
          ]}
        >
          <FlexItem
            modifiers={[
              FlexLayoutModifier.GROW
            ]}
          >
            <button
              type="button"
              className="u-els-anchorize"
              onClick={props.handleReturnNavigation}
            >
              <IconWithText
                iconName="arrow-left"
                iconPrefix="gizmo"
                iconPosition={ELSButtonIconPosition.LEFT}
              >
                Back to all recommendations
              </IconWithText>
            </button>
          </FlexItem>
          <FlexItem>
            <StatusPill status={status}>
              {StatusDisplayMap[status]}
            </StatusPill>
          </FlexItem>
        </FlexLayout>

      </div>

      <div className="c-ssa-case-study-overview__body">

        <h2>
          <strong>{getTitle()}</strong>
        </h2>
        <div className="o-els-container">
          <table className="c-ssa-remediation-goal-modal__goal-table">
            <tbody>
              <tr>
                <td>
                  <IconWithText iconName="syllabus" iconPrefix="hmds">Instructions</IconWithText>
                </td>
                {
                  contentTypeConfig.isScored && (
                    <td>You must reach a score of {getScoreThreshold()} or above on this {contentTypeConfig.displayName}</td>
                  )
                }
                {
                  !contentTypeConfig.isScored && (
                    <td>You must complete the {contentTypeConfig.displayName}</td>
                  )
                }
              </tr>
              {
                props.recommendation.contentType === RecommendationContentTypeDto.EBOOK_READING
                && recData
                && recData.title
                && (
                  <tr>
                    <td>
                      <IconWithText iconName="information-outline-square" iconPrefix="gizmo">Excerpt from</IconWithText>
                    </td>
                    <td>
                      {getEbookTitleWithPagesDisplay(props.recommendation)}
                    </td>
                  </tr>
                )
              }
              <tr>
                <td>
                  <IconWithText iconName="clock" iconPrefix="gizmo">Est. time to complete</IconWithText>
                </td>
                <td>
                  {getTime()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="o-els-container">
          <FlexLayout modifiers={[FlexLayoutModifier.LEFT]}>
            <FlexItem>
              <ELSButton type={ELSButtonType.PRIMARY}
                         size={ELSButtonSize.SMALL}
                         onClick={() => {
                           setAppState((prev) => ({ ...prev, isLaunchingRecommendation: true }));
                           handleResumeOrStartNewAttempt({
                             appState,
                             appStateRef,
                             setAppState,
                             recommendation: props.recommendation,
                             recommendationAttempt: inProgressAttempt,
                             baseState,
                             history,
                             ref: RoutePath.REMEDIATION_STUDENT_HOME
                           }).catch(() => setAppState((prev) => ({ ...prev, isLaunchingRecommendation: false })));
                         }}>
                {inProgressAttempt ? 'Continue latest attempt' : 'Start new attempt'}
              </ELSButton>
            </FlexItem>
          </FlexLayout>
        </div>

        <div className="o-els-container o-els-container--2x c-els-divider" />

        {tableData && (
          <div>
            <h3>{contentTypeConfig.displayName} performance</h3>

            <div>
              <ELSDataTable
                data={tableData}
                noWrap
                sortIconSize="1o2"
                columns={[
                  {
                    header: "Date started",
                    field: "createdAt",
                    sortable: true,
                    customRender: (row: TableDataRow) => tableDataRowHandler([], <span>{moment(row.createdAt).format(DATE_TIME_PRIMARY)}</span>)
                  },
                  {
                    header: "Status",
                    field: "status",
                    sortable: true,
                    customRender: (row: TableDataRow) => tableDataRowHandler([], <span>{StatusDisplayMap[row.statusWithDueDate]}</span>)
                  },
                  ...(!RemediationContentTypeConfigMap[props.recommendation.contentType].isScored ? [{
                    header: "Score",
                    field: "score",
                    sortable: true,
                    customRender: (row: TableDataRow) => tableDataRowHandler([!row.score, <></>], <span>{`${Math.round(row.score * 100)}%`}</span>)
                  }] : []),
                  {
                    header: " ",
                    customRender: (row: TableDataRow) => navigateButtonToContent(row)
                  }
                ]}
              >


              </ELSDataTable>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const enhance = flowRight(ELSWithModalService);

const RecommendationOverview:React.FC<RecommendationOverviewPropsOnly> = enhance(RecommendationOverviewComponent);

export default RecommendationOverview;
